/**
 * Maintenance API Service
 * 
 * Comprehensive service for communicating with the maintenance planning API endpoints.
 * Provides full CRUD operations, filtering, search, and dashboard functionality.
 */

import {
  MaintenanceSchedule,
  MaintenanceWorkOrder,
  MaintenanceRoute,
  MaintenanceRouteWorkOrder,
  RoutineServiceWork,
  RoutineServiceWorkOrder,
  MaintenanceRecord,
  MaintenancePart,
  MaintenanceDashboard,
  MaintenanceScheduleSummary,
  MaintenanceRouteSummary,
  PaginatedResponse,
  MaintenanceScheduleFilters,
  MaintenanceWorkOrderFilters,
  MaintenanceRouteFilters,
  CreateMaintenanceSchedule,
  CreateMaintenanceWorkOrder,
  CreateMaintenanceRoute,
  BulkWorkOrderUpdate
} from '../types/maintenance';

import {
  TaskDataField,
  CreateTaskDataField,
  TaskDataResponse
} from '../types';

declare const process: {
  env: {
    REACT_APP_API_URL?: string;
    NODE_ENV?: string;
  };
};

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';
const BASE_URL = `${API_BASE_URL}/maintenance`;

// Global CSRF token cache
let csrfToken: string | null = null;

/**
 * Get CSRF token from backend
 */
const getCSRFToken = async (): Promise<string | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/users/auth/csrf_token/`, {
      method: 'GET',
      credentials: 'include',
    });

    if (response.ok) {
      const data = await response.json();
      csrfToken = data.csrf_token;
      return csrfToken;
    }
  } catch (error) {
    console.warn('⚠️ Failed to get CSRF token:', error);
  }
  return null;
};

/**
 * Get headers with CSRF token for authenticated requests
 */
const getHeaders = async (additionalHeaders: HeadersInit = {}): Promise<HeadersInit> => {
  const defaultHeaders: HeadersInit = {
    'Content-Type': 'application/json',
    ...additionalHeaders,
  };

  // Get auth token from localStorage if available
  const token = localStorage.getItem('authToken');
  if (token) {
    (defaultHeaders as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  // Try to get CSRF token if we don't have one
  if (!csrfToken) {
    await getCSRFToken();
  }

  // Add CSRF token to headers if available
  if (csrfToken) {
    (defaultHeaders as Record<string, string>)['X-CSRFToken'] = csrfToken;
  }

  return defaultHeaders;
};

/**
 * API request helper with proper headers and error handling
 */
const apiRequest = async (url: string, options: RequestInit = {}): Promise<any> => {
  // Get headers with CSRF token
  const headers = await getHeaders(options.headers);

  const config: RequestInit = {
    credentials: 'include', // Include cookies for session management
    headers,
    ...options,
  };

  try {
    const response = await fetch(url, config);
    
    if (!response.ok) {
      const errorData = await response.text();
      
      // If CSRF error, clear token and potentially retry
      if (errorData.includes('CSRF')) {
        console.warn('🔄 CSRF error detected, clearing token');
        csrfToken = null;
      }
      
      throw new Error(`HTTP ${response.status}: ${errorData}`);
    }

    // Handle empty responses (like DELETE)
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return await response.json();
    }
    
    return null;
  } catch (error) {
    console.error('API Request failed:', error);
    throw error;
  }
};

/**
 * Build query parameters string from filters object
 */
const buildQueryParams = (filters: Record<string, any>): string => {
  const params = new URLSearchParams();
  
  Object.keys(filters).forEach((key: string) => {
    const value = filters[key];
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value)) {
        value.forEach((v: any) => params.append(key, v.toString()));
      } else {
        params.append(key, value.toString());
      }
    }
  });
  
  return params.toString();
};

/**
 * Maintenance Schedule API
 */
export const maintenanceScheduleService = {
  /**
   * Get all maintenance schedules with optional filtering
   */
  async getSchedules(filters?: MaintenanceScheduleFilters): Promise<PaginatedResponse<MaintenanceSchedule>> {
    const queryString = filters ? buildQueryParams(filters) : '';
    const url = `${BASE_URL}/schedules/${queryString ? `?${queryString}` : ''}`;
    return await apiRequest(url);
  },

  /**
   * Get a specific maintenance schedule by ID
   */
  async getSchedule(id: number): Promise<MaintenanceSchedule> {
    return await apiRequest(`${BASE_URL}/schedules/${id}/`);
  },

  /**
   * Create a new maintenance schedule
   */
  async createSchedule(schedule: CreateMaintenanceSchedule): Promise<MaintenanceSchedule> {
    return await apiRequest(`${BASE_URL}/schedules/`, {
      method: 'POST',
      body: JSON.stringify(schedule),
    });
  },

  /**
   * Update an existing maintenance schedule
   */
  async updateSchedule(id: number, schedule: Partial<CreateMaintenanceSchedule>): Promise<MaintenanceSchedule> {
    return await apiRequest(`${BASE_URL}/schedules/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(schedule),
    });
  },

  /**
   * Delete a maintenance schedule
   */
  async deleteSchedule(id: number): Promise<void> {
    await apiRequest(`${BASE_URL}/schedules/${id}/`, {
      method: 'DELETE',
    });
  },

  /**
   * Get overdue schedules
   */
  async getOverdueSchedules(): Promise<MaintenanceScheduleSummary[]> {
    return await apiRequest(`${BASE_URL}/schedules/overdue/`);
  },

  /**
   * Get schedules due soon
   */
  async getDueSoonSchedules(): Promise<MaintenanceScheduleSummary[]> {
    return await apiRequest(`${BASE_URL}/schedules/due-soon/`);
  }
};

/**
 * Maintenance Work Order API
 */
export const maintenanceWorkOrderService = {
  /**
   * Get all work orders with optional filtering
   */
  async getWorkOrders(filters?: MaintenanceWorkOrderFilters): Promise<PaginatedResponse<MaintenanceWorkOrder>> {
    const queryString = filters ? buildQueryParams(filters) : '';
    const url = `${BASE_URL}/work-orders/${queryString ? `?${queryString}` : ''}`;
    return await apiRequest(url);
  },

  /**
   * Get a specific work order by ID
   */
  async getWorkOrder(id: number): Promise<MaintenanceWorkOrder> {
    return await apiRequest(`${BASE_URL}/work-orders/${id}/`);
  },

  /**
   * Create a new work order
   */
  async createWorkOrder(workOrder: CreateMaintenanceWorkOrder): Promise<MaintenanceWorkOrder> {
    return await apiRequest(`${BASE_URL}/work-orders/`, {
      method: 'POST',
      body: JSON.stringify(workOrder),
    });
  },

  /**
   * Update an existing work order
   */
  async updateWorkOrder(id: number, workOrder: Partial<CreateMaintenanceWorkOrder>): Promise<MaintenanceWorkOrder> {
    return await apiRequest(`${BASE_URL}/work-orders/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(workOrder),
    });
  },

  /**
   * Delete a work order
   */
  async deleteWorkOrder(id: number): Promise<void> {
    await apiRequest(`${BASE_URL}/work-orders/${id}/`, {
      method: 'DELETE',
    });
  },

  /**
   * Start a work order
   */
  async startWorkOrder(id: number): Promise<{ status: string }> {
    return await apiRequest(`${BASE_URL}/work-orders/${id}/start/`, {
      method: 'POST',
    });
  },

  /**
   * Complete a work order
   */
  async completeWorkOrder(id: number): Promise<{ status: string }> {
    return await apiRequest(`${BASE_URL}/work-orders/${id}/complete/`, {
      method: 'POST',
    });
  },

  /**
   * Bulk update work orders
   */
  async bulkUpdateWorkOrders(update: BulkWorkOrderUpdate): Promise<{ status: string }> {
    return await apiRequest(`${BASE_URL}/work-orders/bulk-update/`, {
      method: 'POST',
      body: JSON.stringify(update),
    });
  }
};

/**
 * Task Data Fields API (replaces Task Definitions after consolidation)
 * These are now managed in the sites app as part of StandardTask configuration
 */
export const taskDataFieldService = {
  /**
   * Get all task data fields with optional filtering
   */
  async getTaskDataFields(filters?: { 
    standard_task?: number; 
    input_type?: string; 
    page?: number; 
    page_size?: number; 
  }): Promise<PaginatedResponse<TaskDataField>> {
    const queryString = filters ? buildQueryParams(filters) : '';
    const url = `/api/v1/sites/task-data-fields/${queryString ? `?${queryString}` : ''}`;
    return await apiRequest(url);
  },

  /**
   * Get a specific task data field by ID
   */
  async getTaskDataField(id: number): Promise<TaskDataField> {
    return await apiRequest(`/api/v1/sites/task-data-fields/${id}/`);
  },

  /**
   * Create a new task data field
   */
  async createTaskDataField(taskField: CreateTaskDataField): Promise<TaskDataField> {
    return await apiRequest(`/api/v1/sites/task-data-fields/`, {
      method: 'POST',
      body: JSON.stringify(taskField),
    });
  },

  /**
   * Update an existing task data field
   */
  async updateTaskDataField(id: number, taskField: Partial<CreateTaskDataField>): Promise<TaskDataField> {
    return await apiRequest(`/api/v1/sites/task-data-fields/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(taskField),
    });
  },

  /**
   * Delete a task data field
   */
  async deleteTaskDataField(id: number): Promise<void> {
    await apiRequest(`/api/v1/sites/task-data-fields/${id}/`, {
      method: 'DELETE',
    });
  }
};

/**
 * Maintenance Route API
 */
export const maintenanceRouteService = {
  /**
   * Get all maintenance routes with optional filtering
   */
  async getRoutes(filters?: MaintenanceRouteFilters): Promise<PaginatedResponse<MaintenanceRoute>> {
    const queryString = filters ? buildQueryParams(filters) : '';
    const url = `${BASE_URL}/routes/${queryString ? `?${queryString}` : ''}`;
    return await apiRequest(url);
  },

  /**
   * Get a specific maintenance route by ID
   */
  async getRoute(id: number): Promise<MaintenanceRoute> {
    return await apiRequest(`${BASE_URL}/routes/${id}/`);
  },

  /**
   * Create a new maintenance route
   */
  async createRoute(route: CreateMaintenanceRoute): Promise<MaintenanceRoute> {
    return await apiRequest(`${BASE_URL}/routes/`, {
      method: 'POST',
      body: JSON.stringify(route),
    });
  },

  /**
   * Update an existing maintenance route
   */
  async updateRoute(id: number, route: Partial<CreateMaintenanceRoute>): Promise<MaintenanceRoute> {
    return await apiRequest(`${BASE_URL}/routes/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(route),
    });
  },

  /**
   * Delete a maintenance route
   */
  async deleteRoute(id: number): Promise<void> {
    await apiRequest(`${BASE_URL}/routes/${id}/`, {
      method: 'DELETE',
    });
  },

  /**
   * Get route summaries
   */
  async getRouteSummaries(filters?: MaintenanceRouteFilters): Promise<MaintenanceRouteSummary[]> {
    const queryString = filters ? buildQueryParams(filters) : '';
    const url = `${BASE_URL}/routes/summaries/${queryString ? `?${queryString}` : ''}`;
    return await apiRequest(url);
  }
};

/**
 * Maintenance Route Work Order API
 */
export const maintenanceRouteWorkOrderService = {
  /**
   * Get all route work orders with optional filtering
   */
  async getRouteWorkOrders(filters?: { status?: string; site?: string }): Promise<PaginatedResponse<MaintenanceRouteWorkOrder>> {
    const queryString = filters ? buildQueryParams(filters) : '';
    const url = `${BASE_URL}/route-work-orders/${queryString ? `?${queryString}` : ''}`;
    return await apiRequest(url);
  },

  /**
   * Get a specific route work order by ID
   */
  async getRouteWorkOrder(id: number): Promise<MaintenanceRouteWorkOrder> {
    return await apiRequest(`${BASE_URL}/route-work-orders/${id}/`);
  },

  /**
   * Create a new route work order
   */
  async createRouteWorkOrder(routeWorkOrder: Partial<MaintenanceRouteWorkOrder>): Promise<MaintenanceRouteWorkOrder> {
    return await apiRequest(`${BASE_URL}/route-work-orders/`, {
      method: 'POST',
      body: JSON.stringify(routeWorkOrder),
    });
  },

  /**
   * Update an existing route work order
   */
  async updateRouteWorkOrder(id: number, routeWorkOrder: Partial<MaintenanceRouteWorkOrder>): Promise<MaintenanceRouteWorkOrder> {
    return await apiRequest(`${BASE_URL}/route-work-orders/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(routeWorkOrder),
    });
  },

  /**
   * Delete a route work order
   */
  async deleteRouteWorkOrder(id: number): Promise<void> {
    await apiRequest(`${BASE_URL}/route-work-orders/${id}/`, {
      method: 'DELETE',
    });
  }
};

/**
 * Maintenance Task Response API
 */
export const maintenanceTaskResponseService = {
  /**
   * Get all task responses with optional filtering
   */
  async getTaskResponses(filters?: { work_order?: number; route_work_order?: number }): Promise<PaginatedResponse<TaskDataResponse>> {
    const queryString = filters ? buildQueryParams(filters) : '';
    const url = `${BASE_URL}/data-responses/${queryString ? `?${queryString}` : ''}`;
    return await apiRequest(url);
  },

  /**
   * Get a specific task data response by ID
   */
  async getTaskResponse(id: number): Promise<TaskDataResponse> {
    return await apiRequest(`${BASE_URL}/data-responses/${id}/`);
  },

  /**
   * Create a new task data response
   */
  async createTaskResponse(taskResponse: Partial<TaskDataResponse>): Promise<TaskDataResponse> {
    return await apiRequest(`${BASE_URL}/data-responses/`, {
      method: 'POST',
      body: JSON.stringify(taskResponse),
    });
  },

  /**
   * Update an existing task data response
   */
  async updateTaskResponse(id: number, taskResponse: Partial<TaskDataResponse>): Promise<TaskDataResponse> {
    return await apiRequest(`${BASE_URL}/data-responses/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(taskResponse),
    });
  },

  /**
   * Delete a task response
   */
  async deleteTaskResponse(id: number): Promise<void> {
    await apiRequest(`${BASE_URL}/task-responses/${id}/`, {
      method: 'DELETE',
    });
  }
};

/**
 * Routine Service Work API
 */
export const routineServiceWorkService = {
  /**
   * Get all routine services with optional filtering
   */
  async getRoutineServices(filters?: { site?: string; service_type?: string }): Promise<PaginatedResponse<RoutineServiceWork>> {
    const queryString = filters ? buildQueryParams(filters) : '';
    const url = `${BASE_URL}/routine-services/${queryString ? `?${queryString}` : ''}`;
    return await apiRequest(url);
  },

  /**
   * Get a specific routine service by ID
   */
  async getRoutineService(id: number): Promise<RoutineServiceWork> {
    return await apiRequest(`${BASE_URL}/routine-services/${id}/`);
  },

  /**
   * Create a new routine service
   */
  async createRoutineService(routineService: Partial<RoutineServiceWork>): Promise<RoutineServiceWork> {
    return await apiRequest(`${BASE_URL}/routine-services/`, {
      method: 'POST',
      body: JSON.stringify(routineService),
    });
  },

  /**
   * Update an existing routine service
   */
  async updateRoutineService(id: number, routineService: Partial<RoutineServiceWork>): Promise<RoutineServiceWork> {
    return await apiRequest(`${BASE_URL}/routine-services/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(routineService),
    });
  },

  /**
   * Delete a routine service
   */
  async deleteRoutineService(id: number): Promise<void> {
    await apiRequest(`${BASE_URL}/routine-services/${id}/`, {
      method: 'DELETE',
    });
  }
};

/**
 * Routine Service Work Orders API
 */
export const routineServiceWorkOrderService = {
  /**
   * Get all routine service work orders with optional filtering
   */
  async getRoutineServiceWorkOrders(filters?: { status?: string; site?: string }): Promise<PaginatedResponse<RoutineServiceWorkOrder>> {
    const queryString = filters ? buildQueryParams(filters) : '';
    const url = `${BASE_URL}/routine-service-orders/${queryString ? `?${queryString}` : ''}`;
    return await apiRequest(url);
  },

  /**
   * Get a specific routine service work order by ID
   */
  async getRoutineServiceWorkOrder(id: number): Promise<RoutineServiceWorkOrder> {
    return await apiRequest(`${BASE_URL}/routine-service-orders/${id}/`);
  },

  /**
   * Create a new routine service work order
   */
  async createRoutineServiceWorkOrder(workOrder: Partial<RoutineServiceWorkOrder>): Promise<RoutineServiceWorkOrder> {
    return await apiRequest(`${BASE_URL}/routine-service-orders/`, {
      method: 'POST',
      body: JSON.stringify(workOrder),
    });
  },

  /**
   * Update an existing routine service work order
   */
  async updateRoutineServiceWorkOrder(id: number, workOrder: Partial<RoutineServiceWorkOrder>): Promise<RoutineServiceWorkOrder> {
    return await apiRequest(`${BASE_URL}/routine-service-orders/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(workOrder),
    });
  },

  /**
   * Delete a routine service work order
   */
  async deleteRoutineServiceWorkOrder(id: number): Promise<void> {
    await apiRequest(`${BASE_URL}/routine-service-orders/${id}/`, {
      method: 'DELETE',
    });
  }
};

/**
 * Maintenance Record API
 */
export const maintenanceRecordService = {
  /**
   * Get all maintenance records with enhanced filtering
   */
  async getRecords(filters?: {
    work_order?: number;
    equipment?: number;
    maintenance_task?: number;
    completed_by?: number;
    completion_status?: string;
    completed_at_from?: string;
    completed_at_to?: string;
    search?: string;
    ordering?: string;
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<MaintenanceRecord>> {
    const queryString = filters ? buildQueryParams(filters) : '';
    const url = `${BASE_URL}/records/${queryString ? `?${queryString}` : ''}`;
    return await apiRequest(url);
  },

  /**
   * Get a specific maintenance record by ID with related data
   */
  async getRecord(id: number): Promise<MaintenanceRecord> {
    return await apiRequest(`${BASE_URL}/records/${id}/`);
  },

  /**
   * Get maintenance records with task data responses for analysis
   */
  async getRecordsWithData(filters?: {
    equipment?: number;
    maintenance_task?: number;
    data_field?: number;
    completed_at_from?: string;
    completed_at_to?: string;
  }): Promise<any[]> {
    const queryString = filters ? buildQueryParams(filters) : '';
    const url = `${BASE_URL}/records/with_data/${queryString ? `?${queryString}` : ''}`;
    return await apiRequest(url);
  },

  /**
   * Get time series data for specific data fields
   */
  async getTimeSeriesData(dataFieldIds: number[], filters?: {
    equipment?: number;
    maintenance_task?: number;
    completed_at_from?: string;
    completed_at_to?: string;
  }): Promise<{
    field_id: number;
    field_label: string;
    unit_of_measure: string;
    data_points: Array<{
      timestamp: string;
      value: number;
      record_id: number;
      equipment_name: string;
    }>;
  }[]> {
    const params = {
      data_fields: dataFieldIds.join(','),
      ...filters
    };
    const queryString = buildQueryParams(params);
    const url = `${BASE_URL}/records/time_series/${queryString ? `?${queryString}` : ''}`;
    return await apiRequest(url);
  },

  /**
   * Get maintenance data points for charting (new method)
   */
  async getMaintenanceDataPoints(filters: {
    date_from: string;
    date_to: string;
    equipment?: string;
    task?: string;
    measurement?: string;
    site?: string;
    record_ids?: number[];
  }): Promise<{
    id: number;
    equipment_name: string;
    task_name: string;
    measurement_name: string;
    measurement_value: number;
    unit: string;
    recorded_at: string;
    technician_name: string;
    status: string;
  }[]> {
    const params = {
      ...filters,
      record_ids: filters.record_ids?.length ? filters.record_ids.join(',') : undefined
    };
    const queryString = buildQueryParams(params);
    const url = `${BASE_URL}/records/data_points/${queryString ? `?${queryString}` : ''}`;
    return await apiRequest(url);
  },

  /**
   * Create a new maintenance record
   */
  async createRecord(record: Partial<MaintenanceRecord>): Promise<MaintenanceRecord> {
    return await apiRequest(`${BASE_URL}/records/`, {
      method: 'POST',
      body: JSON.stringify(record),
    });
  },

  /**
   * Update an existing maintenance record
   */
  async updateRecord(id: number, record: Partial<MaintenanceRecord>): Promise<MaintenanceRecord> {
    return await apiRequest(`${BASE_URL}/records/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(record),
    });
  },

  /**
   * Delete a maintenance record
   */
  async deleteRecord(id: number): Promise<void> {
    await apiRequest(`${BASE_URL}/records/${id}/`, {
      method: 'DELETE',
    });
  },

  /**
   * Generate PDF report for a maintenance record
   */
  async generateRecordPDF(id: number): Promise<Blob> {
    const response = await fetch(`${API_BASE_URL}/maintenance/records/${id}/generate_pdf/`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
      },
    });
    
    if (!response.ok) {
      throw new Error('Failed to generate PDF');
    }
    
    return await response.blob();
  },

  /**
   * Generate comparison PDF for multiple records
   */
  async generateComparisonPDF(recordIds: number[]): Promise<Blob> {
    const response = await fetch(`${API_BASE_URL}/maintenance/records/generate_comparison_pdf/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
      },
      body: JSON.stringify({ record_ids: recordIds }),
    });
    
    if (!response.ok) {
      throw new Error('Failed to generate comparison PDF');
    }
    
    return await response.blob();
  }
};

/**
 * Maintenance Part API
 */
export const maintenancePartService = {
  /**
   * Get all maintenance parts with optional filtering
   */
  async getParts(filters?: { work_order?: number; supplier?: string }): Promise<PaginatedResponse<MaintenancePart>> {
    const queryString = filters ? buildQueryParams(filters) : '';
    const url = `${BASE_URL}/parts/${queryString ? `?${queryString}` : ''}`;
    return await apiRequest(url);
  },

  /**
   * Get a specific maintenance part by ID
   */
  async getPart(id: number): Promise<MaintenancePart> {
    return await apiRequest(`${BASE_URL}/parts/${id}/`);
  },

  /**
   * Create a new maintenance part
   */
  async createPart(part: Partial<MaintenancePart>): Promise<MaintenancePart> {
    return await apiRequest(`${BASE_URL}/parts/`, {
      method: 'POST',
      body: JSON.stringify(part),
    });
  },

  /**
   * Update an existing maintenance part
   */
  async updatePart(id: number, part: Partial<MaintenancePart>): Promise<MaintenancePart> {
    return await apiRequest(`${BASE_URL}/parts/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(part),
    });
  },

  /**
   * Delete a maintenance part
   */
  async deletePart(id: number): Promise<void> {
    await apiRequest(`${BASE_URL}/parts/${id}/`, {
      method: 'DELETE',
    });
  }
};

/**
 * Dashboard API
 */
export const maintenanceDashboardService = {
  /**
   * Get dashboard data with optional site filtering
   */
  async getDashboardData(siteCode?: string): Promise<MaintenanceDashboard> {
    const queryString = siteCode ? `?site=${siteCode}` : '';
    return await apiRequest(`${BASE_URL}/dashboard/${queryString}`);
  }
};

/**
 * Combined maintenance service for easier importing
 */
export const maintenanceService = {
  schedules: maintenanceScheduleService,
  workOrders: maintenanceWorkOrderService,
  taskDataFields: taskDataFieldService,
  routes: maintenanceRouteService,
  routeWorkOrders: maintenanceRouteWorkOrderService,
  taskResponses: maintenanceTaskResponseService,
  routineServices: routineServiceWorkService,
  routineServiceWorkOrders: routineServiceWorkOrderService,
  records: maintenanceRecordService,
  parts: maintenancePartService,
  dashboard: maintenanceDashboardService,

  // New methods for hierarchical overdue task management
  overdueTasks: {
    /**
     * Get maintenance tasks with overdue assignments (work packages)
     */
    async getOverdueMaintenanceTasks(siteId?: string) {
      const params = new URLSearchParams();
      if (siteId) params.append('site', siteId);

      const response = await fetch(`${API_BASE_URL}/maintenance/tasks/?${params}`);
      const data = await response.json();
      
      // Filter tasks that have overdue assignments
      const overdueTasks = data.results?.filter((task: any) => 
        task.has_overdue_assignments || task.is_overdue
      ) || [];
      
      return {
        results: overdueTasks,
        count: overdueTasks.length
      };
    },

    /**
     * Get standalone overdue assignments (not part of any maintenance task)
     */
    async getStandaloneOverdueAssignments(siteId?: string) {
      const params = new URLSearchParams();
      if (siteId) params.append('site', siteId);

      // Get all overdue assignments
      const overdueResponse = await fetch(`${API_BASE_URL}/maintenance/task-assignments/overdue/?${params}`);
      const overdueAssignments = await overdueResponse.json();

      // Get all maintenance tasks to identify which assignments are already grouped
      const tasksResponse = await fetch(`${API_BASE_URL}/maintenance/tasks/?${params}`);
      const tasksData = await tasksResponse.json();
      
      // Build set of assignment IDs that are part of maintenance tasks
      const taskAssignmentIds = new Set();
      tasksData.results?.forEach((task: any) => {
        task.task_assignments_detail?.forEach((assignment: any) => {
          taskAssignmentIds.add(assignment.id);
        });
      });
      
      // Filter out assignments that are already part of maintenance tasks
      const standaloneAssignments = overdueAssignments.filter((assignment: any) => 
        !taskAssignmentIds.has(assignment.id)
      );

      return standaloneAssignments;
    },

    /**
     * Complete a maintenance task (all overdue assignments within it)
     */
    async completeMaintenanceTask(taskId: string, completionDate?: string) {
      const response = await fetch(`${API_BASE_URL}/maintenance/tasks/${taskId}/complete_task/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          completion_date: completionDate || new Date().toISOString().split('T')[0]
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to complete maintenance task');
      }

      return response.json();
    },

    /**
     * Complete a specific assignment within a maintenance task
     */
    async completeTaskAssignment(taskId: string, assignmentId: string, completionDate?: string) {
      const response = await fetch(`${API_BASE_URL}/maintenance/tasks/${taskId}/complete_assignment/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          assignment_id: assignmentId,
          completion_date: completionDate || new Date().toISOString().split('T')[0]
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to complete task assignment');
      }

      return response.json();
    },

    /**
     * Complete a standalone assignment (not part of any maintenance task)
     */
    async completeStandaloneAssignment(assignmentId: string, completionDate?: string) {
      // TODO: Implement backend endpoint for standalone assignment completion
      // For now, this would need to be handled via a work order or direct assignment update
      const response = await fetch(`${API_BASE_URL}/maintenance/task-assignments/${assignmentId}/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          last_completed_date: completionDate || new Date().toISOString().split('T')[0],
          // This would need backend logic to calculate next_due_date based on frequency
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to complete standalone assignment');
      }

      return response.json();
    },

    /**
     * Complete maintenance with detailed results and data collection
     */
    async completeMaintenanceWithResults(
      taskId: string, 
      assignmentId: string | null, 
      resultsData: any, 
      mode: 'task' | 'assignment'
    ) {
      // First create a work order for the completion
      const workOrderData = {
        title: `${mode === 'task' ? 'Task' : 'Assignment'} Completion`,
        description: resultsData.work_performed,
        status: 'completed',
        completion_notes: resultsData.completion_notes,
        findings: resultsData.findings,
        recommendations: resultsData.recommendations,
        completed_at: new Date().toISOString(),
        maintenance_task: mode === 'task' ? taskId : undefined,
        task_assignment: mode === 'assignment' ? assignmentId : undefined,
      };

      const workOrderResponse = await fetch(`${API_BASE_URL}/maintenance/work-orders/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(workOrderData),
      });

      if (!workOrderResponse.ok) {
        throw new Error('Failed to create work order');
      }

      const workOrder = await workOrderResponse.json();

      // Submit data responses if any
      if (resultsData.data_responses && resultsData.data_responses.length > 0) {
        const dataResponsePromises = resultsData.data_responses.map(async (response: any) => {
          const responseData = {
            work_order: workOrder.id,
            data_field: response.data_field_id,
            response_value: response.response_value,
            numeric_value: response.numeric_value,
            boolean_value: response.boolean_value,
            date_value: response.date_value,
            time_value: response.time_value,
            datetime_value: response.datetime_value,
            file_path: response.file_path,
          };

          return fetch(`${API_BASE_URL}/maintenance/data-responses/`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(responseData),
          });
        });

        await Promise.all(dataResponsePromises);
      }

      // Create maintenance record for historical tracking
      const recordData = {
        work_order: workOrder.id,
        work_performed: resultsData.work_performed,
        findings: resultsData.findings,
        issues_discovered: resultsData.issues_discovered,
        parts_replaced: resultsData.parts_replaced,
        recommendations: resultsData.recommendations,
        follow_up_required: resultsData.follow_up_required,
        follow_up_notes: resultsData.follow_up_notes,
        work_quality_rating: resultsData.work_quality_rating,
      };

      const recordResponse = await fetch(`${API_BASE_URL}/maintenance/records/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(recordData),
      });

      // Finally, complete the actual task/assignment
      if (mode === 'task') {
        await this.completeMaintenanceTask(taskId);
      } else if (assignmentId) {
        await this.completeTaskAssignment(taskId, assignmentId);
      }

      return {
        workOrder,
        record: recordResponse.ok ? await recordResponse.json() : null,
      };
    }
  },

  // Method to get task data fields for a given assignment or task
  dataFields: {
    /**
     * Get data fields for a specific standard task
     */
    async getTaskDataFields(standardTaskId: string) {
      const response = await fetch(`${API_BASE_URL}/sites/task-data-fields/?standard_task=${standardTaskId}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch task data fields');
      }

      return response.json();
    },

    /**
     * Get data fields for a task assignment
     */
    async getAssignmentDataFields(assignmentId: string) {
      // First get the assignment to find the standard task
      const assignmentResponse = await fetch(`${API_BASE_URL}/maintenance/task-assignments/${assignmentId}/`);
      
      if (!assignmentResponse.ok) {
        throw new Error('Failed to fetch assignment');
      }

      const assignment = await assignmentResponse.json();
      return this.getTaskDataFields(assignment.standard_task);
    }
  },

  // Enhanced completion workflow APIs
  assignTechnician: async (taskId: string, assignmentData: any) => {
    const response = await apiRequest(`${BASE_URL}/tasks/${taskId}/assign_technician/`, {
      method: 'POST',
      body: JSON.stringify(assignmentData),
    });
    return response;
  },

  saveCompletionDraft: async (taskId: string, draftData: any) => {
    const response = await apiRequest(`${BASE_URL}/tasks/${taskId}/save_completion_draft/`, {
      method: 'POST',
      body: JSON.stringify(draftData),
    });
    return response;
  },

  getCompletionDraft: async (taskId: string) => {
    const response = await apiRequest(`${BASE_URL}/tasks/${taskId}/get_completion_draft/`);
    return response;
  },

  completeTaskComprehensive: async (taskId: string, completionData: any) => {
    const response = await apiRequest(`${BASE_URL}/tasks/${taskId}/complete_comprehensive/`, {
      method: 'POST',
      body: JSON.stringify(completionData),
    });
    return response;
  },

  getAvailableTechnicians: async (taskId: string) => {
    const response = await apiRequest(`${BASE_URL}/tasks/${taskId}/available_technicians/`);
    return response;
  },

  // Work order management
  createWorkOrderFromTask: async (taskId: string, workOrderData: any) => {
    const response = await apiRequest(`${BASE_URL}/work-orders/`, {
      method: 'POST',
      body: JSON.stringify({
        ...workOrderData,
        maintenance_task: taskId,
      }),
    });
    return response;
  },

  updateWorkOrderStatus: async (workOrderId: number, status: string) => {
    const response = await apiRequest(`${BASE_URL}/work-orders/${workOrderId}/`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
    return response;
  },

  getWorkOrdersByTask: async (taskId: string) => {
    const response = await apiRequest(`${BASE_URL}/work-orders/?maintenance_task=${taskId}`);
    return response;
  },

  // Maintenance records
  getMaintenanceRecords: async (taskId?: string) => {
    const url = taskId 
      ? `${BASE_URL}/records/?maintenance_task=${taskId}`
      : `${BASE_URL}/records/`;
    const response = await apiRequest(url);
    return response;
  },

  getMaintenanceRecord: async (recordId: number) => {
    const response = await apiRequest(`${BASE_URL}/records/${recordId}/`);
    return response;
  }
};

export default maintenanceService; 